import type { SurveyResults } from '~/gql/surveys/surveys'
import { useTranslation } from 'react-i18next'
import { StatTile } from '~/components/StatTile'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { tourAnchor } from '~/helpers/tour/anchors'

/** Une moyenne sur 5, avec le nombre de notes qui la font. */
function Average({ label, average, count }: { label: string, average: number, count: number }) {
  const { t } = useTranslation()
  return (
    <li className="flex items-baseline justify-between gap-4 text-sm">
      <span className="min-w-0">{label}</span>
      <span className="shrink-0 tabular-nums text-muted-foreground">{t('survey.results.average', { average, count })}</span>
    </li>
  )
}

/**
 * Ce que les apprenants ont dit du parcours (#320), sans aucun nom : taux de
 * satisfaction avec sa méthode (Qualiopi, indicateur 2), moyennes par question,
 * évaluation des contenus module par module (V10, indicateur 33), commentaires.
 */
function SessionSurveyCard({ results, formatDate }: { results: SurveyResults, formatDate: (value: unknown) => string }) {
  const { t } = useTranslation()
  const offered = results.answered + results.skipped

  return (
    <Card {...tourAnchor('sessionDetail.survey')}>
      <CardHeader>
        <CardTitle className="text-lg">{t('survey.results.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {offered === 0
          ? <p className="text-sm text-muted-foreground">{t('survey.results.empty')}</p>
          : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <StatTile
                    label={t('survey.results.satisfaction')}
                    value={results.satisfactionRate == null ? '—' : `${results.satisfactionRate} %`}
                    hint={t('survey.results.satisfactionMethod')}
                  />
                  <StatTile
                    label={t('survey.results.responses')}
                    value={results.answered}
                    hint={t('survey.results.skipped', { count: results.skipped })}
                  />
                </div>
                {results.questions.length > 0 && (
                  <section className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold">{t('survey.results.questions')}</h3>
                    <ul className="flex flex-col gap-1.5">{results.questions.map(q => <Average key={q.questionId} label={q.label} average={q.average} count={q.count} />)}</ul>
                  </section>
                )}
                {results.modules.length > 0 && (
                  <section className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold">{t('survey.results.modules')}</h3>
                    <ul className="flex flex-col gap-1.5">{results.modules.map(m => <Average key={m.moduleId} label={m.title} average={m.average} count={m.count} />)}</ul>
                  </section>
                )}
                {results.comments.length > 0 && (
                  <section className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold">{t('survey.results.comments')}</h3>
                    <ul className="flex max-h-72 flex-col gap-3 overflow-y-auto">
                      {results.comments.map(c => (
                        <li key={`${String(c.at)}-${c.text}`} className="rounded-md bg-muted p-3 text-sm">
                          <p className="whitespace-pre-line">{c.text}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{`${c.label} · ${formatDate(c.at)}`}</p>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
      </CardContent>
    </Card>
  )
}

export default SessionSurveyCard
